import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Edit2, Trash2, Power, Tag, IndianRupee, Calendar, Users, X,
  CheckCircle2, AlertCircle, TrendingUp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

const fmtMoney = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const EMPTY_FORM = {
  code: '',
  discount_type: 'percent',
  discount_value: 10,
  min_order_amount: 0,
  max_discount_amount: '',
  start_date: '',
  expiry_date: '',
  usage_limit: '',
  applicable_services: '',
  is_active: true,
  description: '',
  first_order_only: false,
};

function CouponForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value) || 0,
      min_order_amount: parseInt(form.min_order_amount || 0, 10),
      max_discount_amount: form.max_discount_amount === '' ? null : parseInt(form.max_discount_amount, 10),
      start_date: form.start_date || null,
      expiry_date: form.expiry_date || null,
      usage_limit: form.usage_limit === '' ? null : parseInt(form.usage_limit, 10),
      applicable_services: form.applicable_services
        ? form.applicable_services.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      is_active: !!form.is_active,
      description: form.description || null,
      first_order_only: !!form.first_order_only,
    };
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm space-y-4" data-testid="coupon-form">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-bold text-gray-900">{initial ? 'Edit Coupon' : 'Create New Coupon'}</h3>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold">Coupon Code *</Label>
          <Input
            value={form.code}
            onChange={(e) => set('code', e.target.value.toUpperCase())}
            placeholder="e.g. SAVE10"
            className="mt-1 uppercase font-mono"
            required
            data-testid="form-code"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold">Discount Type *</Label>
          <select
            value={form.discount_type}
            onChange={(e) => set('discount_type', e.target.value)}
            className="mt-1 w-full h-10 border border-gray-200 rounded-md px-3 text-sm"
            data-testid="form-type"
          >
            <option value="percent">Percentage (%)</option>
            <option value="fixed">Fixed Amount (₹)</option>
          </select>
        </div>
        <div>
          <Label className="text-sm font-semibold">Discount Value *</Label>
          <Input type="number" min={0} step="0.01" value={form.discount_value} onChange={(e) => set('discount_value', e.target.value)} className="mt-1" required data-testid="form-value" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Min Order Amount (₹)</Label>
          <Input type="number" min={0} value={form.min_order_amount} onChange={(e) => set('min_order_amount', e.target.value)} className="mt-1" data-testid="form-min" />
        </div>
        {form.discount_type === 'percent' && (
          <div>
            <Label className="text-sm font-semibold">Max Discount Cap (₹) <span className="text-gray-400 font-normal">optional</span></Label>
            <Input type="number" min={0} value={form.max_discount_amount} onChange={(e) => set('max_discount_amount', e.target.value)} className="mt-1" placeholder="No cap" data-testid="form-max" />
          </div>
        )}
        <div>
          <Label className="text-sm font-semibold">Usage Limit <span className="text-gray-400 font-normal">optional</span></Label>
          <Input type="number" min={0} value={form.usage_limit} onChange={(e) => set('usage_limit', e.target.value)} className="mt-1" placeholder="Unlimited" data-testid="form-limit" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Start Date</Label>
          <Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} className="mt-1" data-testid="form-start" />
        </div>
        <div>
          <Label className="text-sm font-semibold">Expiry Date</Label>
          <Input type="date" value={form.expiry_date} onChange={(e) => set('expiry_date', e.target.value)} className="mt-1" data-testid="form-expiry" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-sm font-semibold">Applicable Service Slugs <span className="text-gray-400 font-normal">comma-separated, leave empty for all</span></Label>
          <Input value={form.applicable_services} onChange={(e) => set('applicable_services', e.target.value)} className="mt-1" placeholder="cockroach-control, ant-control" data-testid="form-services" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-sm font-semibold">Description / Internal Notes</Label>
          <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} className="mt-1 resize-none" placeholder="What is this coupon for?" data-testid="form-description" />
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" id="form-active" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} className="w-4 h-4" data-testid="form-active" />
          <Label htmlFor="form-active" className="cursor-pointer text-sm">Active</Label>
        </div>
        <div className="flex items-center gap-3">
          <input type="checkbox" id="form-first" checked={form.first_order_only} onChange={(e) => set('first_order_only', e.target.checked)} className="w-4 h-4" data-testid="form-first" />
          <Label htmlFor="form-first" className="cursor-pointer text-sm">First Order Only</Label>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700" data-testid="form-save">
          {saving ? 'Saving…' : initial ? 'Update Coupon' : 'Create Coupon'}
        </Button>
      </div>
    </form>
  );
}

function CouponCard({ coupon, onEdit, onDelete, onToggle, onShowUsage }) {
  const used = coupon.used_count || 0;
  const limit = coupon.usage_limit;
  const expired = coupon.expiry_date && new Date(coupon.expiry_date) < new Date();
  const inactive = !coupon.is_active || expired || (limit && used >= limit);

  return (
    <div className={`bg-white rounded-xl border-2 p-5 shadow-sm transition-all ${inactive ? 'border-gray-200 opacity-75' : 'border-green-200'}`} data-testid={`coupon-card-${coupon.code}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${inactive ? 'bg-gray-100 text-gray-400' : 'bg-green-100 text-green-700'}`}>
            <Tag size={20} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-lg font-extrabold tracking-wider text-gray-900 font-mono">{coupon.code}</span>
              {coupon.is_active ? (
                <span className="text-[10px] uppercase font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Active</span>
              ) : (
                <span className="text-[10px] uppercase font-bold bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Disabled</span>
              )}
              {expired && <span className="text-[10px] uppercase font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">Expired</span>}
            </div>
            <div className="text-sm text-gray-600 mt-0.5">
              {coupon.discount_type === 'percent'
                ? `${coupon.discount_value}% off`
                : `${fmtMoney(coupon.discount_value)} off`}
              {coupon.max_discount_amount ? ` · max ${fmtMoney(coupon.max_discount_amount)}` : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={onToggle} className="p-2 rounded-md hover:bg-gray-100 text-gray-600" title="Toggle active" data-testid={`coupon-toggle-${coupon.code}`}><Power size={15} /></button>
          <button onClick={onEdit} className="p-2 rounded-md hover:bg-blue-50 text-blue-600" title="Edit" data-testid={`coupon-edit-${coupon.code}`}><Edit2 size={15} /></button>
          <button onClick={onDelete} className="p-2 rounded-md hover:bg-red-50 text-red-600" title="Delete" data-testid={`coupon-delete-${coupon.code}`}><Trash2 size={15} /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-gray-600 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5"><IndianRupee size={12} /> Min ₹{coupon.min_order_amount || 0}</div>
        <div className="flex items-center gap-1.5"><Users size={12} /> {used}{limit ? ` / ${limit}` : ' used'}</div>
        <div className="flex items-center gap-1.5"><Calendar size={12} /> {formatDate(coupon.expiry_date) === '—' ? 'No expiry' : `exp. ${formatDate(coupon.expiry_date)}`}</div>
        <button
          onClick={onShowUsage}
          className="text-blue-700 hover:bg-blue-50 hover:text-blue-800 text-left flex items-center justify-center gap-1.5 px-2 py-1 rounded-md border border-blue-200 bg-blue-50/50 font-semibold transition"
          data-testid={`coupon-usage-${coupon.code}`}
          title={`View ${used} redemption${used === 1 ? '' : 's'}`}
        >
          <TrendingUp size={12} /> View redemptions ({used})
        </button>
      </div>
      {coupon.description && <p className="mt-2 text-xs text-gray-500 italic">{coupon.description}</p>}
      {coupon.applicable_services?.length > 0 && (
        <p className="mt-2 text-[11px] text-gray-500">
          Applies to: <span className="font-medium">{coupon.applicable_services.join(', ')}</span>
        </p>
      )}
    </div>
  );
}

function UsageModal({ coupon, onClose, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API_BASE}/admin/coupons/${coupon.id}/usage`, { headers: { 'X-Admin-Token': token } })
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => mounted && setData({ items: [], total_used: 0, total_discount: 0 }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [coupon.id, token]);

  return (
    <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="usage-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">Redemptions — <span className="font-mono">{coupon.code}</span></h3>
            <p className="text-sm text-gray-500">
              {loading ? 'Loading…' : `${data?.total_used || 0} redemption(s) · ${fmtMoney(data?.total_discount || 0)} total discount given`}
            </p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          {loading && <div className="text-center py-8 text-gray-500">Loading…</div>}
          {!loading && (data?.items || []).length === 0 && (
            <div className="text-center py-8 text-gray-500" data-testid="no-redemptions">No redemptions yet.</div>
          )}
          {(data?.items || []).map((u) => (
            <div key={u.id} className="bg-gray-50 hover:bg-emerald-50 ring-1 ring-transparent hover:ring-emerald-200 rounded-lg p-3 text-sm transition" data-testid={`redemption-row-${u.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-900 flex flex-wrap items-center gap-2">
                    <span data-testid={`redemption-name-${u.id}`}>{u.customer_name || 'Anonymous'}</span>
                    {u.customer_phone && (
                      <>
                        <a
                          href={`tel:${u.customer_phone}`}
                          className="text-xs font-medium text-blue-600 hover:underline inline-flex items-center gap-1"
                          data-testid={`redemption-call-${u.id}`}
                          title={`Call ${u.customer_phone}`}
                        >📞 {u.customer_phone}</a>
                        <a
                          href={`https://api.whatsapp.com/send?phone=91${(u.customer_phone || '').replace(/\D/g, '').slice(-10)}&text=${encodeURIComponent(`Hi ${u.customer_name || ''}, this is regarding your booking with EntoRid (coupon ${u.coupon_code})`)}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-medium text-emerald-700 hover:underline inline-flex items-center gap-1"
                          data-testid={`redemption-wa-${u.id}`}
                          title="WhatsApp"
                        >💬 WhatsApp</a>
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{formatDate(u.used_at)} · Payment {u.payment_id}</div>
                  {u.lead_id && <div className="text-[11px] text-gray-400 font-mono mt-0.5">Lead: {u.lead_id}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-green-700 font-bold">−{fmtMoney(u.discount_amount)}</div>
                  <div className="text-xs text-gray-500">on {fmtMoney(u.original_amount)}</div>
                  <div className="text-xs text-emerald-600 font-semibold mt-1">Paid {fmtMoney(u.final_amount)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// All Redemptions Modal — Aggregated view across ALL coupons
// Lets admin instantly see WHO has used which coupon, with
// click-to-call / WhatsApp / open-specific-coupon shortcuts.
// ============================================================
function AllRedemptionsModal({ coupons, token, onClose, onOpenCoupon }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const all = await Promise.all(coupons.map(c =>
          axios.get(`${API_BASE}/admin/coupons/${c.id}/usage`, { headers: { 'X-Admin-Token': token } })
            .then(r => (r.data?.items || []).map(u => ({ ...u, coupon_code: c.code, coupon_id: c.id })))
            .catch(() => [])
        ));
        if (alive) {
          const flat = all.flat().sort((a, b) => (b.used_at || '').localeCompare(a.used_at || ''));
          setRows(flat);
        }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [coupons, token]);

  const filtered = rows.filter(r => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.customer_name || '').toLowerCase().includes(s)
      || (r.customer_phone || '').toLowerCase().includes(s)
      || (r.customer_email || '').toLowerCase().includes(s)
      || (r.coupon_code || '').toLowerCase().includes(s);
  });
  const totalDiscount = filtered.reduce((a, r) => a + (r.discount_amount || 0), 0);

  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="all-redemptions-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold">All Coupon Redemptions</h3>
            <p className="text-sm text-gray-500">{loading ? 'Loading…' : `${filtered.length} redemption(s) · ${fmtMoney(totalDiscount)} total discount`}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center" data-testid="all-red-close"><X size={18} /></button>
        </div>
        <div className="px-4 py-3 border-b bg-gray-50">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone, email, or coupon code…" className="w-full h-9 px-3 rounded-md border border-gray-300 text-sm bg-white" data-testid="all-red-search" />
        </div>
        <div className="overflow-y-auto p-4 space-y-2">
          {loading && <div className="text-center py-8 text-gray-500">Loading customers who redeemed coupons…</div>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-10 text-gray-500" data-testid="all-red-empty">No redemptions yet. When a customer applies a coupon during booking, they will appear here.</div>
          )}
          {filtered.map((u) => (
            <div key={u.id} className="bg-gray-50 hover:bg-emerald-50 ring-1 ring-transparent hover:ring-emerald-200 rounded-lg p-3 text-sm transition" data-testid={`all-red-row-${u.id}`}>
              <div className="flex justify-between items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-gray-900">{u.customer_name || 'Anonymous'}</span>
                    <button
                      onClick={() => onOpenCoupon(coupons.find(c => c.id === u.coupon_id))}
                      className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200"
                      title="Open this coupon's full redemption list"
                      data-testid={`all-red-coupon-${u.id}`}
                    >{u.coupon_code}</button>
                    {u.customer_phone && (
                      <>
                        <a href={`tel:${u.customer_phone}`} className="text-xs font-medium text-blue-600 hover:underline">📞 {u.customer_phone}</a>
                        <a href={`https://api.whatsapp.com/send?phone=91${(u.customer_phone || '').replace(/\D/g, '').slice(-10)}&text=${encodeURIComponent(`Hi ${u.customer_name || ''}, thanks for using coupon ${u.coupon_code} with EntoRid!`)}`} target="_blank" rel="noreferrer" className="text-xs font-medium text-emerald-700 hover:underline">💬 WhatsApp</a>
                      </>
                    )}
                  </div>
                  {u.customer_email && <div className="text-xs text-gray-500 mt-0.5">{u.customer_email}</div>}
                  <div className="text-xs text-gray-500 mt-1">{formatDate(u.used_at)} · Payment {u.payment_id}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-green-700 font-bold">−{fmtMoney(u.discount_amount)}</div>
                  <div className="text-xs text-gray-500">on {fmtMoney(u.original_amount)}</div>
                  <div className="text-xs text-emerald-600 font-semibold mt-1">Paid {fmtMoney(u.final_amount)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminCoupons({ token }) {
  const [coupons, setCoupons] = useState([]);
  const [showAllRedemptions, setShowAllRedemptions] = useState(false);
  const [stats, setStats] = useState({ total_coupons: 0, active_coupons: 0, total_redemptions: 0, total_discount_given: 0 });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [usageCoupon, setUsageCoupon] = useState(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const headers = { 'X-Admin-Token': token };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API_BASE}/admin/coupons`, { headers }),
        axios.get(`${API_BASE}/admin/coupons-stats`, { headers }),
      ]);
      setCoupons(a.data || []);
      setStats(b.data || {});
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Failed to load coupons');
    } finally { setLoading(false); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (token) fetchAll(); }, [token, fetchAll]);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API_BASE}/admin/coupons/${editing.id}`, payload, { headers });
        showToast('success', `Coupon ${payload.code} updated`);
      } else {
        await axios.post(`${API_BASE}/admin/coupons`, payload, { headers });
        showToast('success', `Coupon ${payload.code} created`);
      }
      setShowForm(false);
      setEditing(null);
      await fetchAll();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon ${coupon.code}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_BASE}/admin/coupons/${coupon.id}`, { headers });
      showToast('success', `Coupon ${coupon.code} deleted`);
      await fetchAll();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleToggle = async (coupon) => {
    try {
      await axios.patch(`${API_BASE}/admin/coupons/${coupon.id}`, { is_active: !coupon.is_active }, { headers });
      showToast('success', `Coupon ${coupon.code} ${coupon.is_active ? 'disabled' : 'activated'}`);
      await fetchAll();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Toggle failed');
    }
  };

  const handleEdit = (coupon) => {
    setEditing(coupon);
    setShowForm(true);
  };

  const initialEditForm = editing ? {
    ...EMPTY_FORM,
    ...editing,
    max_discount_amount: editing.max_discount_amount ?? '',
    usage_limit: editing.usage_limit ?? '',
    start_date: (editing.start_date || '').slice(0, 10),
    expiry_date: (editing.expiry_date || '').slice(0, 10),
    applicable_services: (editing.applicable_services || []).join(', '),
    description: editing.description || '',
  } : null;

  return (
    <div data-testid="admin-coupons-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <button
          type="button"
          onClick={() => setShowAllRedemptions(true)}
          className="text-left bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-blue-300 hover:shadow-md transition cursor-pointer"
          data-testid="stat-coupons-total"
          title="See all coupons and who redeemed them"
        >
          <p className="text-xs text-gray-500 mb-1 uppercase font-semibold tracking-wider">Total Coupons</p>
          <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.total_coupons}</p>
          <p className="text-[10px] text-blue-600 font-semibold mt-1">Click to view all redemptions →</p>
        </button>
        <div className="bg-white p-5 rounded-xl border border-gray-100 shadow-sm" data-testid="stat-coupons-active">
          <p className="text-xs text-gray-500 mb-1 uppercase font-semibold tracking-wider">Active</p>
          <p className="text-2xl sm:text-3xl font-bold text-green-600">{stats.active_coupons}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowAllRedemptions(true)}
          className="text-left bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:border-blue-300 hover:shadow-md transition cursor-pointer"
          data-testid="stat-coupons-redemptions"
          title="See exactly who used coupons"
        >
          <p className="text-xs text-gray-500 mb-1 uppercase font-semibold tracking-wider">Redemptions</p>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">{stats.total_redemptions}</p>
          <p className="text-[10px] text-blue-600 font-semibold mt-1">Click to view customers →</p>
        </button>
        <button
          type="button"
          onClick={() => setShowAllRedemptions(true)}
          className="text-left bg-gradient-to-br from-green-600 to-emerald-700 p-5 rounded-xl text-white shadow-sm hover:shadow-lg transition cursor-pointer"
          data-testid="stat-coupons-discount"
          title="See per-customer discount breakdown"
        >
          <p className="text-xs text-green-100 mb-1 uppercase font-semibold tracking-wider">Discount Given</p>
          <p className="text-2xl sm:text-3xl font-bold">{fmtMoney(stats.total_discount_given)}</p>
          <p className="text-[10px] text-green-100 font-semibold mt-1">Click to see breakdown →</p>
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-xl font-bold text-gray-900">Coupon Management</h2>
        {!showForm && (
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-green-600 hover:bg-green-700" data-testid="add-coupon-btn">
            <Plus size={16} className="mr-1.5" /> New Coupon
          </Button>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mb-4 rounded-lg p-3 text-sm flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="coupon-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="mb-6">
          <CouponForm
            initial={initialEditForm}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setEditing(null); }}
            saving={saving}
          />
        </div>
      )}

      {/* List */}
      {loading && coupons.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Loading coupons…</div>
      ) : coupons.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-12 text-center" data-testid="coupons-empty">
          <Tag size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-2">No coupons yet.</p>
          <Button onClick={() => setShowForm(true)} className="mt-3 bg-green-600 hover:bg-green-700">
            <Plus size={16} className="mr-1.5" /> Create your first coupon
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="coupons-list">
          {coupons.map((c) => (
            <CouponCard
              key={c.id}
              coupon={c}
              onEdit={() => handleEdit(c)}
              onDelete={() => handleDelete(c)}
              onToggle={() => handleToggle(c)}
              onShowUsage={() => setUsageCoupon(c)}
            />
          ))}
        </div>
      )}

      {usageCoupon && <UsageModal coupon={usageCoupon} onClose={() => setUsageCoupon(null)} token={token} />}
      {showAllRedemptions && <AllRedemptionsModal coupons={coupons} token={token} onClose={() => setShowAllRedemptions(false)} onOpenCoupon={(c) => { setShowAllRedemptions(false); setUsageCoupon(c); }} />}
    </div>
  );
}
